import React, { useState } from 'react';
import { useStyles } from '../context/StylesContext';
import { StyleOption } from '../context/StylesContext';

export default function WritingStylesPage() {
  const { styles, addStyle, editStyle, removeStyle, loading, error } = useStyles();
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [editingStyle, setEditingStyle] = useState<StyleOption | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!value.trim() || !label.trim()) return;
    
    setIsSubmitting(true);
    setActionError(null);
    
    try {
      await addStyle({ 
        value: value.trim(), 
        label: label.trim(),
        description: description.trim() || undefined
      });
      setValue('');
      setLabel('');
      setDescription('');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to add style');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (style: StyleOption) => {
    setEditingStyle({ ...style });
    setIsModalOpen(true);
    setActionError(null);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingStyle?.id) return;
    
    setIsSubmitting(true);
    setActionError(null);
    
    try {
      await editStyle(editingStyle.id, {
        value: editingStyle.value,
        label: editingStyle.label,
        description: editingStyle.description
      });
      setIsModalOpen(false);
      setEditingStyle(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update style');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemove = async (style: StyleOption) => {
    if (!style.id) return;
    
    if (style.isDefault) {
      setActionError('Cannot delete default writing styles');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete "${style.label}"?`)) {
      setActionError(null);
      try {
        await removeStyle(style.id);
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Failed to delete style');
      }
    }
  };

  const handleCancelEdit = () => {
    setIsModalOpen(false);
    setEditingStyle(null);
    setActionError(null);
  };

  if (loading) {
    return (
      <div className="px-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-300">Loading writing styles...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">Writing Styles</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          Add, edit, or remove available writing styles with descriptions
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4 mb-8">
        {(error || actionError) && (
          <div className="p-3 bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-600 rounded-md">
            <p className="text-red-700 dark:text-red-400 text-sm">
              {actionError || error}
            </p>
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-4">
          <input
            type="text"
            placeholder="Value (e.g., casual)"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={isSubmitting}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
          />
          <input
            type="text"
            placeholder="Label (e.g., Casual)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            disabled={isSubmitting}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
          />
        </div>
        <textarea
          placeholder="Description (optional) - Explain when and how to use this writing style"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          disabled={isSubmitting}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={isSubmitting || !value.trim() || !label.trim()}
          className="self-start px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Adding...' : 'Add Style'}
        </button>
      </form>

      <ul className="space-y-2">
        {styles.map((style) => (
          <li
            key={style.id || style.value}
            className="bg-white dark:bg-gray-800 p-4 rounded shadow"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {style.label}
                  </h3>
                  {style.isDefault && (
                    <span className="text-xs bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 px-2 py-1 rounded">
                      Default
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Value: {style.value}
                </p>
                {style.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                    {style.description}
                  </p>
                )}
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => handleEdit(style)}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleRemove(style)}
                  disabled={style.isDefault}
                  className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={style.isDefault ? 'Cannot delete default styles' : 'Delete this style'}
                >
                  Remove
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {/* Edit Modal */}
      {isModalOpen && editingStyle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Edit Writing Style
            </h2>
            {actionError && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-600 rounded-md">
                <p className="text-red-700 dark:text-red-400 text-sm">
                  {actionError}
                </p>
              </div>
            )}
            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Value
                </label>
                <input
                  type="text"
                  value={editingStyle.value}
                  onChange={(e) => setEditingStyle({...editingStyle, value: e.target.value})}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Label
                </label>
                <input
                  type="text"
                  value={editingStyle.label}
                  onChange={(e) => setEditingStyle({...editingStyle, label: e.target.value})}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={editingStyle.description || ''}
                  onChange={(e) => setEditingStyle({...editingStyle, description: e.target.value || undefined})}
                  rows={4}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none disabled:opacity-50"
                  placeholder="Explain when and how to use this writing style"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting || !editingStyle.value.trim() || !editingStyle.label.trim()}
                  className="flex-1 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

